/**
 * manual-content.tsx — contenido del manual de usuario, segmentado por rol.
 * Estático y declarativo: cada sección es un paso con icono + descripción.
 * La página lo renderiza; agregar/editar pasos no toca el layout.
 */
import {
  FilePlus2,
  WifiOff,
  RefreshCw,
  PenLine,
  FileCheck2,
  Users,
  Building2,
  Palette,
  LayoutDashboard,
  type LucideIcon,
} from "lucide-react";
import type { Rol } from "@/features/auth/index.js";

export interface ManualStep {
  icon: LucideIcon;
  title: string;
  body: string;
}

export interface ManualSection {
  title: string;
  intro: string;
  steps: ManualStep[];
}

const TECNICO: ManualSection[] = [
  {
    title: "Crear un informe",
    intro: "Cada visita a campo empieza eligiendo el tipo de informe.",
    steps: [
      {
        icon: FilePlus2,
        title: "Elige el tipo",
        body: "Desde «¿Qué levantamos hoy?» toca COMENZAR en Informe de Evaluación (IE), Informe de Servicio (IS) o Acta de Conformidad (AC). Cada tipo abre el formulario correcto.",
      },
      {
        icon: PenLine,
        title: "Completa los datos",
        body: "Llena cliente, fotos, actividades y conclusiones. El informe se guarda solo cada pocos segundos: no pierdes avance si cierras la app.",
      },
      {
        icon: FileCheck2,
        title: "Finaliza",
        body: "Al terminar, el informe pasa a la cola de envío y, con señal, se sincroniza y genera el PDF para el cliente.",
      },
    ],
  },
  {
    title: "Trabajar sin señal",
    intro: "La app funciona offline. Puedes levantar informes completos en zonas sin internet.",
    steps: [
      {
        icon: WifiOff,
        title: "Todo se guarda local",
        body: "Fotos, firmas y datos quedan en el dispositivo. El indicador «EN COLA» arriba a la derecha te dice cuántos informes esperan envío.",
      },
      {
        icon: RefreshCw,
        title: "Se sincroniza al recuperar señal",
        body: "Cuando vuelve el internet, los informes en cola se envían automáticamente. Si quieres forzarlo, usa el botón «Sincronizar».",
      },
    ],
  },
  {
    title: "Firmas y entrega",
    intro: "Las Actas de Conformidad necesitan la firma del cliente.",
    steps: [
      {
        icon: PenLine,
        title: "Firma digital",
        body: "En el AC, el cliente firma directamente en la pantalla. La firma se guarda con el informe, también sin señal.",
      },
      {
        icon: FileCheck2,
        title: "Descargar el PDF",
        body: "Desde el detalle del informe puedes ver o descargar el PDF. Sin señal verás una vista previa; con señal, el PDF final del servidor.",
      },
    ],
  },
];

const ADMIN: ManualSection[] = [
  {
    title: "Panel de administración",
    intro: "Desde un solo lugar gestionas informes, usuarios, clientes y la marca.",
    steps: [
      {
        icon: LayoutDashboard,
        title: "Pestañas del panel",
        body: "Usa las pestañas Informes, Usuarios, Clientes y Marca para moverte. Las tarjetas superiores resumen totales, sincronizados, en cola y borradores.",
      },
      {
        icon: FilePlus2,
        title: "Nuevo informe",
        body: "El botón «Nuevo informe» abre el mismo flujo que usa el técnico para crear un informe desde el panel.",
      },
    ],
  },
  {
    title: "Usuarios",
    intro: "Da de alta a los técnicos y administra sus accesos.",
    steps: [
      {
        icon: Users,
        title: "Crear y editar",
        body: "«Crear usuario» abre el formulario; el mismo botón lo cierra. En cada fila, «Editar» permite cambiar nombre, correo, rol y contraseña.",
      },
      {
        icon: FileCheck2,
        title: "Activar / desactivar",
        body: "Desactiva a un usuario para quitarle el acceso sin borrar su historial. Puedes reactivarlo cuando vuelva.",
      },
    ],
  },
  {
    title: "Clientes y marca",
    intro: "Mantén el catálogo de clientes y la identidad de los informes.",
    steps: [
      {
        icon: Building2,
        title: "Clientes",
        body: "Agrega clientes con nombre y sigla opcional (se deriva del nombre si la dejas vacía). El mismo botón abre y cierra el formulario.",
      },
      {
        icon: Palette,
        title: "Marca",
        body: "En la pestaña Marca configuras logo y plantillas que aparecen en los PDF generados.",
      },
    ],
  },
];

export function manualParaRol(rol: Rol): ManualSection[] {
  return rol === "admin" ? ADMIN : TECNICO;
}
